import Daypart from '@/Enums/Daypart.enum';
import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import ReportingDashboardApiTab from '@/Enums/ReportingDashboard/ApiTab.enum';
import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import ExtraBeeswaxFilterControlName from '@/Enums/ReportingDashboard/ExtraFilterControlName.enum';
import { CHART_COLORS, DEFAULT_CHART_COLORS, PRIMARY } from '@/constants/color';
import moment from 'moment';
import { Weekdays } from '../dateFormatter';
import { createObjWithUniqueFields } from '../mapping';

export const getDefaultFilter = () => ({
  // ...getDefaultDateRange(7),
  // TODO: rename dateStart -> dateRangeStart, dateEnd -> dateRangeEnd.
  // Double-check that adswizz API requests still send dateStart and dateEnd OR
  // update API to dateRangeStart and dateRangeEnd
  dateStart: moment().subtract({ days: 6 }).format(DateFormat.Iso8601),
  dateEnd: moment().format(DateFormat.Iso8601),
  dateRangeFormat: FilterDate.LAST_7_DAYS,
  whitelabels: [],
  clients: [],
  placement: [],
  advertisers: [],
  campaigns: [],
  // 'campaign level' filters
  [ExtraBeeswaxFilterControlName.ClientCode]: [],
  [ExtraBeeswaxFilterControlName.ProductCode]: [],
  [ExtraBeeswaxFilterControlName.Estimate]: [],
  [ExtraBeeswaxFilterControlName.Geo]: [],
  [ExtraBeeswaxFilterControlName.CashTrade]: [],
});

const getChartUpdateData = processedReportData => {
  const labels = [];
  const data = [];
  for (const [key, val] of Object.entries(processedReportData)) {
    labels.push(key);
    data.push(val);
  }

  return { labels, data };
};

export const getOmnichannelVerticalGraphData = (reportType, reportData, loading) => {
  const chartData = {
    labels: [],
    datasets: [
      {
        label: 'Impressions',
        data: [],
        backgroundColor: [PRIMARY],
        borderColor: 'white',
      },
    ],
  };
  if (!reportData || loading) return { chartData, csvData: {}, loading };

  const objOfData = createObjWithUniqueFields(reportData, reportType);

  switch (reportType) {
    case BeeswaxReportProperties.DAY_OF_WEEK:
      chartData.labels = Weekdays;
      chartData.datasets[0].data = Weekdays.map(item => objOfData[item] || 0);
      break;
    case BeeswaxReportProperties.DAYPART: {
      const dayparts = Object.values(Daypart);
      chartData.labels = dayparts;
      chartData.datasets[0].data = dayparts.map(item => objOfData[item] || 0);
      break;
    }
    default: {
      const { labels, data } = getChartUpdateData(objOfData);
      chartData.labels = labels;
      chartData.datasets[0].data = data;
      break;
    }
  }
  return { chartData, csvData: objOfData, loading };
};

export const getOmnichannelHorizontalGraphData = (reportType, reportData, loading) => {
  const chartData = {
    labels: [],
    datasets: [
      {
        label: 'Impressions',
        data: [],
        backgroundColor: [PRIMARY],
        borderColor: 'white',
      },
    ],
  };
  if (!reportData || loading) return { chartData, csvData: {}, loading };

  const objOfData = createObjWithUniqueFields(reportData, reportType === 'Creative' ? 'Creative Name' : reportType);

  if (reportType === 'Creative') {
    const labels = [];
    const data = [];
    reportData.forEach(item => {
      labels.push(item['Creative Name']);
      data.push(item.Impressions);
    });
    chartData.labels = labels;
    chartData.datasets[0].data = data;
  } else {
    const { labels, data } = getChartUpdateData(objOfData);
    chartData.labels = labels;
    chartData.datasets[0].data = data;
  }

  return { chartData, csvData: objOfData, loading };
};

export const getOmnichannelDoughnutGraphData = (reportType, reportData, loading) => {
  const chartData = {
    labels: [],
    datasets: [
      {
        label: 'Impressions',
        data: [],
        backgroundColor: [],
        borderColor: 'white',
      },
    ],
  };
  if (!reportData || loading) return { chartData, csvData: {}, loading };

  const objOfData = createObjWithUniqueFields(reportData, reportType);

  const usedColors = new Set();

  for (const [key, val] of Object.entries(objOfData)) {
    chartData.labels.push(key);
    let chartColor = CHART_COLORS[key];
    if (!chartColor) {
      chartColor = DEFAULT_CHART_COLORS.find(color => !usedColors.has(color));
    }
    if (chartColor) {
      usedColors.add(chartColor);
    }
    chartData.datasets[0].backgroundColor.push(chartColor);
    chartData.datasets[0].data.push(val);
  }

  return { chartData, csvData: objOfData, loading };
};

export const getDoohVerticalGraphData = (reportType, reportData, loading) => {
  return getOmnichannelVerticalGraphData(reportType, reportData, loading);
};

export const getDoohHorizontalGraphData = (reportType, reportData, loading) => {
  return getOmnichannelHorizontalGraphData(reportType, reportData, loading);
};

export const getGoogleDoughnutGraphData = (reportType, reportData, loading) => {
  return getOmnichannelDoughnutGraphData(reportType, reportData, loading);
};

export const getGoogleVerticalGraphData = (reportType, reportData, loading) => {
  return getOmnichannelVerticalGraphData(reportType, reportData, loading);
};

export const getGoogleHorizontalGraphData = (reportType, reportData, loading) => {
  return getOmnichannelHorizontalGraphData(reportType, reportData, loading);
};

const FILTER_API_FILTERS = {
  [ApiTabIndex.Omnichannel]: record => !!record.beeswaxId,
  [ApiTabIndex.Dooh]: record => !!record.neuronId,
  [ApiTabIndex.Google]: record => !!record.hasDv360Partner,
  [ApiTabIndex.Search]: record => !!record.hasEsmSemAccount,
  [ApiTabIndex.Social]: record => !!record.hasEsmSclAccount,
  [ApiTabIndex.Adswizz]: record => !!record.adswizzId,
  [ApiTabIndex.Simplifi]: record => !!record.hasSimplifiAccount,
  [ApiTabIndex.Gam]: record => !!record.hasGamAdvertiser,
};

const FILTER_API_MAPPERS = {
  [ApiTabIndex.Omnichannel]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
  [ApiTabIndex.Dooh]: record => ({ ...record, value: record.neuronId }),
  [ApiTabIndex.Google]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
  [ApiTabIndex.Search]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
  [ApiTabIndex.Social]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
  [ApiTabIndex.Adswizz]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
  [ApiTabIndex.Simplifi]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
  [ApiTabIndex.Gam]: record => ({ ...record, value: record.ribeyeAdvertiserId }),
};

// `apiTab` in Redux holds the *visible-tab position* — the index into the
// whitelabel's sorted `visibleReportingScreenTabs` array — not the global
// `ApiTabIndex` enum value. They only coincide when the whitelabel exposes all
// seven tabs in their canonical order; on a whitelabel that hides Search/Social
// (e.g. preview env defaults), the positions shift and a position lookup against
// these enum-keyed dicts silently selects the wrong tab's filter (e.g. Simplifi
// at position 4 hits the Search filter, dropping every Simplifi-linked
// advertiser). Translate via the tab *name* — the only stable identifier
// across whitelabels.
const REPORTING_TAB_TO_API_INDEX = {
  [ReportingDashboardApiTab.Omnichannel]: ApiTabIndex.Omnichannel,
  [ReportingDashboardApiTab.Google]: ApiTabIndex.Google,
  [ReportingDashboardApiTab.Adswizz]: ApiTabIndex.Adswizz,
  [ReportingDashboardApiTab.Dooh]: ApiTabIndex.Dooh,
  [ReportingDashboardApiTab.Search]: ApiTabIndex.Search,
  [ReportingDashboardApiTab.Social]: ApiTabIndex.Social,
  [ReportingDashboardApiTab.Simplifi]: ApiTabIndex.Simplifi,
  [ReportingDashboardApiTab.Gam]: ApiTabIndex.Gam,
};

/** Returns undefined when the position does not address a tab — treat as unresolved, not as a tab. */
export const apiTabPositionToEnum = (visibleTabs, position) => REPORTING_TAB_TO_API_INDEX[visibleTabs?.[position]];

// Reject-all is also what an unresolved tab lands on, which empties the advertiser list
// silently rather than erroring (#4186, still open).
export const filterAdvertisersList = apiTab => FILTER_API_FILTERS[apiTab] ?? (() => false);
export const mapAdvertisersList = apiTab => FILTER_API_MAPPERS[apiTab] ?? (record => record);

export const getTotalImpressions = rows => rows?.reduce((acc, row) => acc + row.Impressions, 0);
